/*
PROV: COHINT037.BF.UIBUTTON
REQ: AUD-UX-10, AUD-UX-11, NAV-02, UX-BASE-05, SYS-ARCH-15
WHY: Shared button primitive — shadcn/ui v4 pattern with brand token mapping.
*/

import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/utils";

const buttonVariants = cva(
  "m-0 box-border border-0 bg-transparent inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer outline-none focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50 dark:focus-visible:border-brand-600 dark:focus-visible:ring-brand-600/50 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: "bg-brand-900 text-white shadow-sm hover:bg-brand-900/90 dark:bg-brand-50 dark:text-brand-900 dark:hover:bg-brand-50/90",
        destructive: "bg-error-500 text-white shadow-sm hover:bg-error-500/90 focus-visible:ring-error-300/20 dark:bg-error-600 dark:hover:bg-error-600/90",
        outline: "border border-brand-200 bg-white shadow-sm hover:bg-brand-50 hover:text-brand-900 dark:border-brand-700 dark:bg-brand-900 dark:text-brand-100 dark:hover:bg-brand-800 dark:disabled:border-brand-800",
        secondary: "bg-brand-100 text-brand-900 shadow-sm hover:bg-brand-100/80 dark:bg-brand-800 dark:text-brand-100 dark:hover:bg-brand-800/80",
        ghost: "text-brand-900 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-800",
        link: "text-brand-900 underline-offset-4 hover:underline dark:text-brand-100 h-auto px-0 py-0",
      },
      size: {
        sm: "h-8 gap-1.5 rounded-md px-3",
        md: "h-9 px-4 py-2",
        lg: "h-10 rounded-md px-6",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) {
    const Comp = asChild ? Slot : "button";
    const isDisabled = Boolean(disabled || loading);
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), loading && "cursor-progress")}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading ? "true" : undefined}
        {...props}
      >
        {asChild ? children : (
          <>
            {loading ? (
              <span
                aria-hidden="true"
                className="block shrink-0 h-4 w-4 box-border animate-spin rounded-full border-2 border-current border-t-transparent"
              />
            ) : null}
            {children}
          </>
        )}
      </Comp>
    );
  }
);

export { Button, buttonVariants };
